import { colors } from "../colors";
import { getLatestContract, getSalaryOrDefault } from "../contract.util";
import { getSpecificity, matchesFilters, sortBy, } from "../filters";
import { sortLifeCycleEvents } from "../lifecycle-events";
import { DRAFT_TIME_SETTINGS, timeEntryTypeColor, TimeSettings, } from "../model";
import { getContractForDate, getContractForRange } from "./contract.util";
import { dateStringMax, toDateString } from "./date.util";
export function getVenue(venues, venueId) {
    return venues.find(({ id }) => id === venueId) || null;
}
export function getDepartment(venues, id) {
    if (id == null) {
        return { department: null, venue: null };
    }
    for (const venue of venues) {
        for (const department of [...venue.departments, ...venue.archivedDepartments]) {
            if (department.id === id) {
                return { department, venue };
            }
        }
    }
    return { venue: null, department: null };
}
export function findPositionById(venues, id) {
    for (const venue of venues) {
        for (const department of [...venue.departments, ...venue.archivedDepartments]) {
            const position = [...department.positions, ...department.archivedPositions].find((r) => r.id === id);
            if (position) {
                return {
                    venue,
                    department,
                    position,
                };
            }
        }
    }
    return null;
}
function findPositionInDepartments(departments, id) {
    for (const department of departments) {
        for (const position of department.positions) {
            if (position.id === id)
                return position;
        }
        for (const position of department.archivedPositions) {
            if (position.id === id)
                return position;
        }
    }
    return null;
}
export function findPositionInVenues(venues, id) {
    for (const venue of venues) {
        const position = findPositionInDepartments(venue.departments, id);
        if (position)
            return position;
        const archivedPosition = findPositionInDepartments(venue.archivedDepartments, id);
        if (archivedPosition)
            return archivedPosition;
    }
    return null;
}
export function getPosition(venues, archivedVenues, id) {
    const res = findPositionById([...venues, ...archivedVenues], id);
    if (res) {
        return {
            ...res,
            label: venues.length > 1 // TODO: Should this be based on only active venues, not all venues?
                ? `${res.venue.name} / ${res.department.name} / ${res.position.name}`
                : `${res.department.name} / ${res.position.name}`,
            color: getPositionColorFromDepartment(res.position, res.department),
        };
    }
    return null;
}
export function getPositionColorFromDepartment(position, department) {
    if (position.color) {
        return position.color;
    }
    const color = (department && department.color) || "";
    return colors[color] || color;
}
export function getPositionColor(venues, position) {
    if (position.color) {
        return position.color;
    }
    const { department } = getDepartment(venues, position.departmentId);
    return getPositionColorFromDepartment(position, department ?? undefined);
}
export function getTimeEntryColor(venues, archivedVenues, entry) {
    const positionData = entry.positionId ? getPosition(venues, archivedVenues, entry.positionId) : null;
    if (positionData) {
        return positionData.color;
    }
    else {
        return timeEntryTypeColor(entry.type);
    }
}
export function getEffectiveStatus(status, lifeCycleEvents) {
    const events = sortLifeCycleEvents(lifeCycleEvents);
    const pastEventsWithStatus = events?.filter((e) => !!e.status && e.date <= toDateString(new Date()));
    return pastEventsWithStatus?.length > 0 ? pastEventsWithStatus.reverse()[0].status : status;
}
export function getTimeSettings(availableTimeSettings, context) {
    if (context.timeEntry && context.timeEntry.employeeId === null) {
        return DRAFT_TIME_SETTINGS;
    }
    const sortedSettings = sortBy(availableTimeSettings, (s) => getSpecificity(s.filters), "descending");
    return sortedSettings.find((s) => matchesFilters(s.filters, context)) || new TimeSettings();
}
export function getTimeSettingsForEntry(availableTimeSettings, company, timeEntry) {
    return getTimeSettings(availableTimeSettings, { company, timeEntry });
}
export function getCostCenter(costCenters, context) {
    return costCenters
        ?.sort((a, b) => getSpecificity(b.entities) - getSpecificity(a.entities))
        ?.find((c) => matchesFilters(c.entities, context));
}
export function getAllCompanyPositions(company) {
    return [...company.venues, ...company.archivedVenues]
        .flatMap((v) => [...v.departments, ...v.archivedDepartments])
        .flatMap((d) => [...d.positions, ...d.archivedPositions]);
}
export function getAllCompanyPositionIds(company) {
    return getAllCompanyPositions(company).map((p) => p.id);
}
/**
 * Returns all position IDs for active positions in a venue.
 * Assumes the input venue is active.
 */
export function getActiveVenuePositionIds(venue) {
    return venue.departments.flatMap((d) => d.positions).map((p) => p.id);
}
export function getAllVenuePositionIds(venue) {
    return [...venue.departments, ...venue.archivedDepartments]
        .flatMap((d) => [...d.positions, ...d.archivedPositions])
        .map((p) => p.id);
}
export function getWageTypeSet(employee, salaryConfigs, context) {
    const wageTypeSets = salaryConfigs;
    wageTypeSets.forEach((set) => (set.entities =
        set.entities || set.employmentTypes.map((t) => ({ type: "employmentType", value: Number(t) }))));
    const contract = !context.date
        ? getLatestContract(employee.contracts)
        : typeof context.date === "string"
            ? getContractForDate(employee.contracts || [], context.date)
            : getContractForRange(employee.contracts || [], context.date);
    const positionId = typeof context.position === "number" ? context.position : context.position?.id;
    const salary = getSalaryOrDefault(positionId, contract?.salaries || []);
    if (salary?.accountingSalaryConfigId) {
        const set = wageTypeSets.find((s) => s.id === salary.accountingSalaryConfigId);
        if (set) {
            return set;
        }
    }
    return wageTypeSets
        .sort((a, b) => getSpecificity(b.entities) - getSpecificity(a.entities))
        .find((s) => matchesFilters(s.entities, context));
}
/**
 * Lower bound for a company's result and issue queries.
 * The result is always set, since `created` backstops the fallback chain.
 */
export function resolveResultsRangeStart(company, from) {
    const { startLedgers, commitTimeEntriesBefore } = company.settings;
    const start = from || startLedgers || toDateString(company.created);
    return commitTimeEntriesBefore ? dateStringMax(start, commitTimeEntriesBefore) : start;
}
//# sourceMappingURL=company.util.js.map